import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box, Typography, TextField, MenuItem, Select,
    Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Avatar, IconButton, Tooltip, InputAdornment, Button,
    TablePagination,
    Checkbox, Snackbar, Alert, CircularProgress, LinearProgress, Skeleton,
} from '@mui/material';
import {
    Search as SearchIcon,
    Visibility as ViewIcon,
    FileDownload as ExportIcon,
    CalendarToday as CalendarIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    UnfoldMore as ExpandAllIcon,
    UnfoldLess as CollapseAllIcon,
    FolderOpen as EmptyIcon,
} from '@mui/icons-material';
import { axiosInstance } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';

const STATUSES = ['All', 'Draft', 'Submitted', 'Approved', 'Rejected', 'Pending Level 2'];
const MONTHS = [
    { value: 0,  label: 'All Months' },
    { value: 1,  label: 'January' },  { value: 2,  label: 'February' },
    { value: 3,  label: 'March' },    { value: 4,  label: 'April' },
    { value: 5,  label: 'May' },      { value: 6,  label: 'June' },
    { value: 7,  label: 'July' },     { value: 8,  label: 'August' },
    { value: 9,  label: 'September' },{ value: 10, label: 'October' },
    { value: 11, label: 'November' }, { value: 12, label: 'December' },
];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = ['All', CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];
const AVATAR_COLORS = ['#4f46e5', '#7c3aed', '#0891b2', '#059669', '#d97706', '#dc2626', '#db2777'];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const normalizeEntry = (e) => ({
    ...e,
    date:        e.entry_date     ?? e.date        ?? '',
    day:         e.entry_date     ? DAY_NAMES[new Date(e.entry_date + 'T00:00:00').getDay()] : (e.day ?? ''),
    project:     e.project_name   ?? e.project      ?? '',
    task:        e.task_name      ?? e.task          ?? '',
    description: e.work_description ?? e.description ?? '',
    hours:       e.hours_worked   ?? e.hours         ?? 0,
    billable:    typeof e.is_billable === 'boolean'
                    ? (e.is_billable ? 'Billable' : 'Non-Billable')
                    : (e.billable ?? 'Non-Billable'),
    overtime:    e.overtime_hours ?? e.overtime      ?? 0,
});

const STATUS_COLORS = {
    Draft:             { bg: '#f1f5f9', color: '#64748b', border: '#cbd5e1' },
    Submitted:         { bg: '#eff6ff', color: '#3b82f6', border: '#93c5fd' },
    Approved:          { bg: '#f0fdf4', color: '#16a34a', border: '#86efac' },
    Rejected:          { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
    'Pending Level 2': { bg: '#fff7ed', color: '#ea580c', border: '#fdba74' },
};

function StatusChip({ status }) {
    const c = STATUS_COLORS[status] || STATUS_COLORS.Draft;
    const label = status === 'Pending Level 2' ? 'Pending External Approval' : status;
    return (
        <Chip label={label} size="small"
            sx={{ bgcolor: c.bg, color: c.color, border: `1px solid ${c.border}`, fontWeight: 700, fontSize: 11 }} />
    );
}

function fmtShort(str) {
    if (!str) return '';
    return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}
function fmtDate(str) {
    if (!str) return '';
    return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}


const selectSx = {
    fontSize: 13, borderRadius: 1.5, height: 36,
    '& .MuiSelect-select': { py: 0.75, fontSize: 13 },
};

export default function AllTimesheetsList({ refreshKey }) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    // Server-side filter state
    const [year,         setYear]         = useState(CURRENT_YEAR);
    const [month,        setMonth]        = useState(0);
    const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || 'All');
    // Client-side filter state
    const [search,       setSearch]       = useState('');
    const [dept,         setDept]         = useState('All');
    // Data state
    const [timesheets,   setTimesheets]   = useState([]);
    const [loading,      setLoading]      = useState(false);
    // Expand / collapse (empty Set = all expanded)
    const [collapsedGroups, setCollapsedGroups] = useState(new Set());
    // Pagination (counts groups)
    const [page,         setPage]         = useState(0);
    const [rowsPerPage,  setRowsPerPage]  = useState(10);
    // Selection
    const [selected,     setSelected]     = useState([]);
    // Snackbar
    const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

    const fetchTimesheets = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(
                API_ENDPOINTS.getAllTimesheets(
                    year !== 'All' ? year : '',
                    month !== 0   ? month : '',
                    statusFilter !== 'All' ? statusFilter : '',
                    '', '', 1, 200,
                )
            );
            const raw = response.data?.result?.data ?? [];
            setTimesheets(raw.map(ts => ({
                ...ts,
                entries: (ts.entries ?? []).map(normalizeEntry),
            })));
            setPage(0);
            setSelected([]);
            setCollapsedGroups(new Set());
        } catch (err) {
            console.error('Error fetching timesheets:', err);
            setSnack({ open: true, msg: 'Failed to load timesheets.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [year, month, statusFilter]);

    useEffect(() => { fetchTimesheets(); }, [fetchTimesheets, refreshKey]);

    useEffect(() => {
        const urlStatus = searchParams.get('status');
        if (urlStatus && STATUSES.includes(urlStatus) && urlStatus !== statusFilter) {
            setStatusFilter(urlStatus);
            setPage(0);
        } else if (!urlStatus && statusFilter !== 'All') {
            setStatusFilter('All');
            setPage(0);
        }
    }, [searchParams, statusFilter]);

    // Departments derived from fetched data
    const departments = useMemo(() => {
        const depts = [...new Set(timesheets.map(t => t.employeeDept).filter(Boolean))].sort();
        return ['All', ...depts];
    }, [timesheets]);

    // Client-side filter (search + dept)
    const filtered = useMemo(() => timesheets.filter(t => {
        if (search && !t.employeeName?.toLowerCase().includes(search.toLowerCase())) return false;
        if (dept !== 'All' && t.employeeDept !== dept) return false;
        return true;
    }), [timesheets, search, dept]);

    // Group by employee
    const groups = useMemo(() => {
        const map = {};
        filtered.forEach(ts => {
            const key = String(ts.employeeId);
            if (!map[key]) {
                map[key] = {
                    employeeId:     key,
                    employeeName:   ts.employeeName,
                    employeeAvatar: ts.employeeAvatar,
                    employeeDept:   ts.employeeDept,
                    employeeTeam:   ts.team_name,
                    timesheets:     [],
                };
            }
            map[key].timesheets.push(ts);
        });
        return Object.values(map);
    }, [filtered]);

    const paginatedGroups = useMemo(
        () => groups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [groups, page, rowsPerPage]
    );

    // Expand / collapse helpers
    const isExpanded   = (id) => !collapsedGroups.has(id);
    const toggleGroup  = (id) => setCollapsedGroups(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const noneCollapsed = groups.every(g => !collapsedGroups.has(g.employeeId));
    const toggleAllGroups = () => {
        if (noneCollapsed) setCollapsedGroups(new Set(groups.map(g => g.employeeId)));
        else setCollapsedGroups(new Set());
    };

    // Selection helpers
    const groupAllIds       = (g) => g.timesheets.map(ts => ts.id);
    const isGroupAllSel     = (g) => { const ids = groupAllIds(g); return ids.length > 0 && ids.every(id => selected.includes(id)); };
    const isGroupPartial    = (g) => { const ids = groupAllIds(g); return ids.some(id => selected.includes(id)) && !isGroupAllSel(g); };
    const toggleGroupSel    = (g) => {
        const ids = groupAllIds(g);
        if (isGroupAllSel(g)) setSelected(prev => prev.filter(id => !ids.includes(id)));
        else setSelected(prev => [...new Set([...prev, ...ids])]);
    };
    const toggleSelect      = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    const allPageIds        = paginatedGroups.flatMap(groupAllIds);
    const allPageSelected   = allPageIds.length > 0 && allPageIds.every(id => selected.includes(id));
    const somePageSelected  = allPageIds.some(id => selected.includes(id));
    const toggleAll         = () => {
        if (allPageSelected) setSelected(prev => prev.filter(id => !allPageIds.includes(id)));
        else setSelected(prev => [...new Set([...prev, ...allPageIds])]);
    };
    const someSelected = selected.length > 0;

    const handleReset = () => {
        setSearch(''); setDept('All'); setStatusFilter('All');
        setMonth(0); setYear(CURRENT_YEAR);
        setPage(0); setSelected([]);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.delete('status');
            return next;
        }, { replace: true });
    };

    const handleExportCSV = () => {
        const exportData = someSelected ? timesheets.filter(ts => selected.includes(ts.id)) : filtered;
        if (!exportData.length) return;
        const headers = ['Employee Name', 'Emp ID', 'Department', 'Manager', 'Week Start', 'Week End', 'Total Hours', 'OT Hours', 'Billable Hours', 'Projects', 'Status', 'Submitted On'];
        const rows = exportData.map(ts => [
            ts.employeeName, ts.employeeCode, ts.employeeDept, ts.managerName ?? '',
            ts.weekStart, ts.weekEnd, ts.totalHours, ts.overtimeHours,
            ts.billableHours, ts.projectCount, ts.status, ts.submittedOn ?? '',
        ]);
        const csv = [
            headers.map(h => `"${h}"`).join(','),
            ...rows.map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')),
        ].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href = url; a.download = `timesheets-${year}-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setSnack({ open: true, msg: `${exportData.length} timesheet(s) exported.`, severity: 'success' });
    };

    return (
        <Box>
            {/* ── Filter Bar ── */}
            <Paper elevation={0} sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2.5, p: 1.75, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                    <FilterIcon sx={{ fontSize: 18, color: '#94a3b8', flexShrink: 0 }} />

                    <TextField size="small" placeholder="Search employee..."
                        value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }}
                        sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: 13 }, '& .MuiInputBase-input': { py: 0.75, fontSize: 13 } }}
                    />

                    <Select size="small" value={dept} onChange={e => { setDept(e.target.value); setPage(0); }}
                        sx={{ ...selectSx, minWidth: 150, bgcolor: '#fff' }}>
                        {departments.map(d => <MenuItem key={d} value={d} sx={{ fontSize: 13 }}>{d === 'All' ? 'All Departments' : d}</MenuItem>)}
                    </Select>

                    <Select size="small" value={month} onChange={e => { setMonth(e.target.value); setPage(0); }}
                        sx={{ ...selectSx, minWidth: 140, bgcolor: '#fff' }}>
                        {MONTHS.map(m => <MenuItem key={m.value} value={m.value} sx={{ fontSize: 13 }}>{m.label}</MenuItem>)}
                    </Select>

                    <Select size="small" value={year} onChange={e => { setYear(e.target.value); setPage(0); }}
                        sx={{ ...selectSx, minWidth: 100, bgcolor: '#fff' }}>
                        {YEARS.map(y => <MenuItem key={y} value={y} sx={{ fontSize: 13 }}>{y}</MenuItem>)}
                    </Select>

                    <Select size="small" value={statusFilter} onChange={e => {
                            const val = e.target.value;
                            setStatusFilter(val);
                            setPage(0);
                            setSearchParams(prev => {
                                const next = new URLSearchParams(prev);
                                if (val === 'All') next.delete('status');
                                else next.set('status', val);
                                return next;
                            }, { replace: true });
                        }}
                        sx={{ ...selectSx, minWidth: 150, bgcolor: '#fff' }}>
                        {STATUSES.map(s => <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{s === 'Pending Level 2' ? 'Pending External Approval' : s}</MenuItem>)}
                    </Select>

                    {/* Expand / Collapse All */}
                    {groups.length > 0 && (
                        <Tooltip title={noneCollapsed ? 'Collapse all groups' : 'Expand all groups'}>
                            <Button size="small" onClick={toggleAllGroups}
                                startIcon={noneCollapsed ? <CollapseAllIcon sx={{ fontSize: 15 }} /> : <ExpandAllIcon sx={{ fontSize: 15 }} />}
                                sx={{ textTransform: 'none', fontSize: 12, color: '#6366f1', flexShrink: 0,
                                    bgcolor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 1.5,
                                    '&:hover': { bgcolor: '#e0e7ff' }, px: 1.25 }}>
                                {noneCollapsed ? 'Collapse All' : 'Expand All'}
                            </Button>
                        </Tooltip>
                    )}

                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={fetchTimesheets} sx={{ color: '#6366f1' }}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Button size="small" onClick={handleReset}
                        sx={{ textTransform: 'none', fontSize: 12, color: '#6366f1', ml: 'auto', flexShrink: 0 }}>
                        Reset Filters
                    </Button>

                    <Tooltip title={filtered.length === 0 ? 'No data to export' : someSelected ? `Export ${selected.length} selected` : `Export all ${filtered.length} filtered`}>
                        <span>
                            <Button size="small" variant="outlined" startIcon={<ExportIcon sx={{ fontSize: 15 }} />}
                                onClick={handleExportCSV} disabled={filtered.length === 0}
                                sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, borderRadius: 1.5, flexShrink: 0,
                                    color: '#0891b2', borderColor: '#a5f3fc', bgcolor: '#ecfeff',
                                    '&:hover': { bgcolor: '#cffafe', borderColor: '#0891b2' },
                                    '&.Mui-disabled': { bgcolor: '#f8fafc', color: '#cbd5e1', borderColor: '#e2e8f0' } }}>
                                {someSelected ? `Export (${selected.length})` : 'Export CSV'}
                            </Button>
                        </span>
                    </Tooltip>
                    {someSelected && (
                        <Button size="small" variant="outlined" onClick={() => setSelected([])}
                            sx={{ textTransform: 'none', fontSize: 12, color: '#64748b', borderColor: '#cbd5e1', flexShrink: 0 }}>
                            Clear
                        </Button>
                    )}
                </Box>
            </Paper>

            {/* ── Cards ── */}
            {loading ? (
                <Box>
                    {[...Array(3)].map((_, gi) => (
                        <Paper key={gi} elevation={0} sx={{ border: '1px solid #e2e8f0', borderLeft: '4px solid #c7d2fe', borderRadius: 2.5, mb: 1.5, overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.75 }}>
                                <Skeleton variant="rectangular" width={20} height={20} sx={{ borderRadius: 0.5, flexShrink: 0 }} />
                                <Skeleton variant="circular" width={38} height={38} sx={{ flexShrink: 0 }} />
                                <Box sx={{ minWidth: 120 }}>
                                    <Skeleton variant="text" width={120} height={16} />
                                    <Skeleton variant="text" width={80} height={12} />
                                </Box>
                                <Skeleton variant="rectangular" width={48} height={32} sx={{ borderRadius: 1.5 }} />
                                <Skeleton variant="rectangular" width={48} height={32} sx={{ borderRadius: 1.5 }} />
                                <Skeleton variant="rectangular" width={70} height={20} sx={{ borderRadius: 2, ml: 1 }} />
                                <Skeleton variant="rectangular" width={70} height={20} sx={{ borderRadius: 2 }} />
                                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Skeleton variant="text" width={72} height={14} />
                                    <Skeleton variant="circular" width={28} height={28} />
                                </Box>
                            </Box>
                        </Paper>
                    ))}
                </Box>
            ) : groups.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 10 }}>
                    <EmptyIcon sx={{ fontSize: 52, color: '#c7d2fe', mb: 1.5 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>No Timesheets Found</Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>Try adjusting your search or filters.</Typography>
                </Box>
            ) : (
                <Box>
                    {/* Select-all row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, px: 0.5 }}>
                        <Checkbox size="small"
                            checked={allPageSelected}
                            indeterminate={somePageSelected && !allPageSelected}
                            onChange={toggleAll}
                            disabled={allPageIds.length === 0}
                            sx={{ color: '#1D4ED8', '&.Mui-checked': { color: '#1D4ED8' }, '&.MuiCheckbox-indeterminate': { color: '#1D4ED8' }, p: 0.5 }}
                        />
                        <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                            {allPageSelected ? 'Deselect all' : 'Select all on this page'} ({allPageIds.length})
                        </Typography>
                    </Box>

                    {/* Employee cards */}
                    {paginatedGroups.map((group, gIdx) => {
                        const expanded    = isExpanded(group.employeeId);
                        const avatarColor = AVATAR_COLORS[gIdx % AVATAR_COLORS.length];

                        const submittedCnt       = group.timesheets.filter(ts => ts.status === 'Submitted').length;
                        const pendingL2Cnt       = group.timesheets.filter(ts => ts.status === 'Pending Level 2').length;
                        const approvedCnt        = group.timesheets.filter(ts => ts.status === 'Approved').length;
                        const rejectedCnt        = group.timesheets.filter(ts => ts.status === 'Rejected').length;
                        const draftCnt           = group.timesheets.filter(ts => ts.status === 'Draft').length;
                        const totalGroupHrs      = group.timesheets.reduce((s, ts) => s + (ts.totalHours || 0), 0);
                        const totalGroupOT       = group.timesheets.reduce((s, ts) => s + (ts.overtimeHours || 0), 0);
                        const totalGroupBillable = group.timesheets.reduce((s, ts) => s + (ts.billableHours || 0), 0);
                        const groupBillableRate  = totalGroupHrs > 0 ? Math.round((totalGroupBillable / totalGroupHrs) * 100) : 0;
                        const totalGroupProjects = group.timesheets.reduce((s, ts) => s + (ts.projectCount || 0), 0);
                        const tsCount            = group.timesheets.length;
                        // avg h/week keeps the number meaningful whether 1 month or 12 months are loaded
                        const avgHrsPerWeek      = tsCount > 0 ? (totalGroupHrs / tsCount) : 0;

                        return (
                            <Paper key={group.employeeId} elevation={0} sx={{
                                border: '1px solid #e2e8f0',
                                borderLeft: `4px solid ${avatarColor}`,
                                borderRadius: 2.5,
                                mb: 1.5,
                                overflow: 'hidden',
                                transition: 'box-shadow 0.15s',
                                '&:hover': { boxShadow: '0 2px 12px rgba(99,102,241,0.08)' },
                            }}>

                                {/* ── Card Header ── */}
                                <Box
                                    onClick={() => toggleGroup(group.employeeId)}
                                    sx={{
                                        px: 2, py: 1.5,
                                        bgcolor: expanded ? '#f8faff' : '#fff',
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: '#f0f4ff' },
                                        transition: 'background 0.15s',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>

                                        {/* Checkbox */}
                                        <Box onClick={e => e.stopPropagation()}>
                                            <Checkbox size="small"
                                                checked={isGroupAllSel(group)}
                                                indeterminate={isGroupPartial(group)}
                                                disabled={tsCount === 0}
                                                onChange={() => toggleGroupSel(group)}
                                                sx={{ color: '#4f46e5', '&.Mui-checked': { color: '#4f46e5' }, '&.MuiCheckbox-indeterminate': { color: '#4f46e5' }, p: 0.5 }}
                                            />
                                        </Box>

                                        {/* Avatar */}
                                        <Avatar sx={{ width: 38, height: 38, bgcolor: avatarColor, fontSize: 13, fontWeight: 700, borderRadius: 2, flexShrink: 0 }}>
                                            {group.employeeAvatar}
                                        </Avatar>

                                        {/* Name + dept · role */}
                                        <Box sx={{ minWidth: 130 }}>
                                            <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#1e293b', lineHeight: 1.2 }}>
                                                {group.employeeName}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
                                                {group.employeeDept}{group.employeeTeam ? ` · ${group.employeeTeam}` : ''}
                                            </Typography>
                                        </Box>

                                        {/* Stat pills */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, ml: 0.5, flexWrap: 'wrap' }}>
                                            {/* Total hours + avg per week — avoids "pileup confusion" on multi-month views */}
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#4f46e5', lineHeight: 1 }}>
                                                    {totalGroupHrs % 1 === 0 ? totalGroupHrs : totalGroupHrs.toFixed(1)}h
                                                </Typography>
                                                <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
                                                    {tsCount > 1
                                                        ? `avg ${avgHrsPerWeek % 1 === 0 ? avgHrsPerWeek : avgHrsPerWeek.toFixed(1)}h/wk`
                                                        : 'total hrs'}
                                                </Typography>
                                            </Box>
                                            {totalGroupOT > 0 && (
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#f59e0b', lineHeight: 1 }}>
                                                        +{totalGroupOT % 1 === 0 ? totalGroupOT : totalGroupOT.toFixed(1)}h
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>OT</Typography>
                                                </Box>
                                            )}
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#059669', lineHeight: 1 }}>
                                                    {groupBillableRate}%
                                                </Typography>
                                                <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>Billable</Typography>
                                            </Box>
                                            <Chip label={`${totalGroupProjects} proj`} size="small"
                                                sx={{ bgcolor: '#eef2ff', color: '#4f46e5', fontWeight: 600, fontSize: 11, height: 20, borderRadius: 1 }} />
                                        </Box>

                                        {/* Status chips */}
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {submittedCnt > 0 && (
                                                <Chip label={`${submittedCnt} pending`} size="small"
                                                    sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 700, fontSize: 10.5, height: 20, borderRadius: 1.5 }} />
                                            )}
                                            {pendingL2Cnt > 0 && (
                                                <Chip label={`${pendingL2Cnt} pending ext.`} size="small"
                                                    sx={{ bgcolor: '#ffedd5', color: '#c2410c', fontWeight: 700, fontSize: 10.5, height: 20, borderRadius: 1.5 }} />
                                            )}
                                            {approvedCnt > 0 && (
                                                <Chip label={`${approvedCnt} approved`} size="small"
                                                    sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700, fontSize: 10.5, height: 20, borderRadius: 1.5 }} />
                                            )}
                                            {rejectedCnt > 0 && (
                                                <Chip label={`${rejectedCnt} rejected`} size="small"
                                                    sx={{ bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 700, fontSize: 10.5, height: 20, borderRadius: 1.5 }} />
                                            )}
                                            {draftCnt > 0 && (
                                                <Chip label={`${draftCnt} draft`} size="small"
                                                    sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 700, fontSize: 10.5, height: 20, borderRadius: 1.5 }} />
                                            )}
                                        </Box>

                                        {/* Timesheet count + expand toggle */}
                                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>
                                                {tsCount} timesheet{tsCount !== 1 ? 's' : ''}
                                            </Typography>
                                            <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
                                                <IconButton size="small"
                                                    onClick={e => { e.stopPropagation(); toggleGroup(group.employeeId); }}
                                                    sx={{ color: '#6366f1', bgcolor: '#eef2ff', '&:hover': { bgcolor: '#e0e7ff' }, borderRadius: 1.5 }}>
                                                    {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* ── Timesheet rows ── */}
                                {expanded && (
                                    <Box sx={{ borderTop: '1px solid #e8eeff' }}>
                                        <TableContainer sx={{ overflowX: 'auto' }}>
                                            <Table size="small" sx={{ minWidth: 700 }}>
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: '#f8faff' }}>
                                                        <TableCell padding="checkbox" sx={{ pl: 2, width: 40 }} />
                                                        {['Week Period', 'Reg Hrs', 'OT Hrs', 'Billable', 'Projects', 'Status', 'Submitted On', 'Actions'].map(h => (
                                                            <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: '#1D4ED8', py: 1, whiteSpace: 'nowrap' }}>{h}</TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {group.timesheets.map((ts, idx) => {
                                                        const isSelected    = selected.includes(ts.id);
                                                        const billableRate  = ts.totalHours > 0 ? Math.round((ts.billableHours / ts.totalHours) * 100) : 0;
                                                        const isLastInGroup = idx === group.timesheets.length - 1;

                                                        return (
                                                            <TableRow key={ts.id}
                                                                selected={isSelected}
                                                                onClick={() => navigate(`/hr/timesheet-management/${ts.id}`, { state: { returnTab: 1 } })}
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    bgcolor: isSelected ? '#eef2ff' : '#fff',
                                                                    '&:hover': { bgcolor: '#f8faff !important' },
                                                                    '&.Mui-selected': { bgcolor: '#eef2ff !important' },
                                                                    '&.Mui-selected:hover': { bgcolor: '#e0e7ff !important' },
                                                                    '& .MuiTableCell-root': { borderBottom: isLastInGroup ? 'none' : '1px solid #f1f5f9' },
                                                                }}>
                                                                <TableCell padding="checkbox" sx={{ pl: 2, borderLeft: `3px solid ${avatarColor}40` }} onClick={e => e.stopPropagation()}>
                                                                    <Checkbox size="small" checked={isSelected}
                                                                        onChange={() => toggleSelect(ts.id)}
                                                                        sx={{ '&.Mui-checked': { color: '#6366f1' } }} />
                                                                </TableCell>

                                                                {/* Week Period */}
                                                                <TableCell>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                        <CalendarIcon sx={{ fontSize: 13, color: '#94a3b8', flexShrink: 0 }} />
                                                                        <Typography sx={{ fontSize: 12, color: '#475569', whiteSpace: 'nowrap' }}>
                                                                            {fmtShort(ts.weekStart)} – {fmtShort(ts.weekEnd)}
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>

                                                                {/* Reg Hrs */}
                                                                <TableCell>
                                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#6366f1' }}>
                                                                        {ts.totalHours}h
                                                                    </Typography>
                                                                </TableCell>

                                                                {/* OT Hrs */}
                                                                <TableCell>
                                                                    <Typography sx={{ fontSize: 13, fontWeight: ts.overtimeHours > 0 ? 700 : 400, color: ts.overtimeHours > 0 ? '#f59e0b' : '#94a3b8' }}>
                                                                        {ts.overtimeHours > 0 ? `+${ts.overtimeHours}h` : '—'}
                                                                    </Typography>
                                                                </TableCell>

                                                                {/* Billable */}
                                                                <TableCell sx={{ minWidth: 90 }}>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                                                        <Typography sx={{ fontSize: 11, color: '#64748b' }}>{ts.billableHours}h</Typography>
                                                                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#059669' }}>{billableRate}%</Typography>
                                                                    </Box>
                                                                    <LinearProgress variant="determinate" value={billableRate}
                                                                        sx={{ height: 4, borderRadius: 2, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#10b981', borderRadius: 2 } }} />
                                                                </TableCell>

                                                                {/* Projects */}
                                                                <TableCell>
                                                                    <Chip label={`${ts.projectCount} proj`} size="small"
                                                                        sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: 11, height: 20, borderRadius: 1 }} />
                                                                </TableCell>

                                                                {/* Status */}
                                                                <TableCell>
                                                                    {!(ts.status === 'Submitted' && ts.resubmission_count) && (
                                                                        <StatusChip status={ts.status} />
                                                                    )}
                                                                    {ts.status === 'Submitted' && !!ts.resubmission_count && (
                                                                        <Chip label="Resubmitted" size="small"
                                                                            sx={{ ml: 0.5, bgcolor: '#eff6ff', color: '#1d4ed8', border: '1px solid #93c5fd', fontWeight: 700, fontSize: 11 }} />
                                                                    )}
                                                                </TableCell>

                                                                {/* Submitted On */}
                                                                <TableCell>
                                                                    <Typography sx={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                                                                        {ts.submittedOn
                                                                            ? fmtDate(ts.submittedOn)
                                                                            : <span style={{ color: '#c4c4c4' }}>Not submitted</span>}
                                                                    </Typography>
                                                                </TableCell>

                                                                {/* Actions */}
                                                                <TableCell onClick={e => e.stopPropagation()}>
                                                                    <Tooltip title="View Details">
                                                                        <IconButton size="small"
                                                                            onClick={() => navigate(`/hr/timesheet-management/${ts.id}`, { state: { returnTab: 1 } })}
                                                                            sx={{ color: '#6366f1', '&:hover': { bgcolor: '#eef2ff' } }}>
                                                                            <ViewIcon sx={{ fontSize: 17 }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                )}
                            </Paper>
                        );
                    })}

                    {/* Pagination */}
                    <Box sx={{ display: 'flex', alignItems: 'center', borderTop: '1px solid #e2e8f0', mt: 0.5, px: 1 }}>
                        <Typography sx={{ fontSize: 11, color: '#94a3b8', flex: 1 }}>
                            {groups.length} employee{groups.length !== 1 ? 's' : ''} · {filtered.length} timesheet{filtered.length !== 1 ? 's' : ''}
                        </Typography>
                        <TablePagination component="div"
                            count={groups.length} page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            labelRowsPerPage="Employees per page:"
                            sx={{ border: 'none', '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: 12 } }} />
                    </Box>
                </Box>
            )}

<Snackbar open={snack.open} autoHideDuration={3500}
                onClose={() => setSnack(p => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnack(p => ({ ...p, open: false }))} severity={snack.severity} sx={{ borderRadius: 2 }}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}
